import { useTranslation } from 'react-i18next'

import type { TargetingTypeId } from '@/services/targeting/targeting'
import { isIpv4AddressOrSubnet, isPrivateOrLocalIpv4 } from '@/utils/ip'
import { isWebUrl, withScheme } from '@/utils/url'
import { TokenInput } from '../TokenInput'
import { NegateToggle } from './NegateToggle'

/** The targeting types whose selection is a plain list of typed-in values. */
export type TokenTargetType = 'domain' | 'url' | 'ipAddress'

export interface TokenTargetValue {
  values: string[]
  /** Undefined for the types the API does not let you negate. */
  negated?: boolean
}

export interface TokenTargetEditorProps {
  type: TokenTargetType
  value: TokenTargetValue
  onChange: (value: TokenTargetValue) => void
}

/** Domain names, article URLs and IP addresses. */
export function TokenTargetEditor({ type, value, onChange }: TokenTargetEditorProps) {
  const { t } = useTranslation()
  const what = t(`targeting.types.${type as TargetingTypeId}.plural`).toLowerCase()

  return (
    <div className="flex flex-col gap-4">
      {value.negated !== undefined && (
        <NegateToggle
          negated={value.negated}
          what={what}
          onChange={(negated) => onChange({ ...value, negated })}
        />
      )}
      <TokenInput
        value={value.values}
        onChange={(values) => onChange({ ...value, values })}
        placeholder={t(`targeting.types.${type as TargetingTypeId}.placeholder`)}
        lowercase={type === 'domain'}
        // A URL is what the ad server matches an article by, so a typed-in
        // `example.com/a` is made absolute and anything that is not a web
        // address is turned away.
        normalize={type === 'url' ? withScheme : undefined}
        validate={
          type === 'url'
            ? (token) => (isWebUrl(token) ? undefined : t('targeting.invalidUrl'))
            : type === 'ipAddress'
              ? // The ad server matches IPv4 only, a `/8`-`/32` subnet included,
                // and never an address an ad request cannot come from.
                (token) => {
                  if (isIpv4AddressOrSubnet(token)) return undefined
                  return isPrivateOrLocalIpv4(token)
                    ? t('targeting.privateIpAddress')
                    : t('targeting.invalidIpAddress')
                }
              : undefined
        }
      />
    </div>
  )
}
